import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminLecturers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "lecturer" });
  const navigate = useNavigate();
  const me = JSON.parse(localStorage.getItem("dm_teacher") || "{}");

  useEffect(() => {
    if (me?.role !== "admin") {
      toast.error("Tato sekce je dostupná jen master adminovi");
      navigate("/teacher");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data);
    } finally {
      setLoading(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/teachers", form);
      toast.success("Účet vytvořen");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "lecturer" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodařilo se vytvořit");
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Smazat účet ${email}?`)) return;
    try {
      await api.delete(`/admin/teachers/${id}`);
      toast.success("Smazáno");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nelze smazat");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/teacher" className="text-slate-500 hover:text-slate-900 text-sm font-bold">← Session</Link>
            <div className="font-heading font-black text-lg tracking-tight text-slate-900">Správa lektorů</div>
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">Admin</span>
          </div>
          <button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2 text-sm" data-testid="btn-add-teacher">
            + Přidat účet
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-slate-500">Načítám…</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Jméno</th>
                  <th className="text-left px-4 py-3 font-bold">E-mail</th>
                  <th className="text-left px-4 py-3 font-bold">Role</th>
                  <th className="text-left px-4 py-3 font-bold">Vytvořen</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`teacher-row-${t.email}`}>
                    <td className="px-4 py-3 font-bold text-slate-900">{t.name}</td>
                    <td className="px-4 py-3 text-slate-600">{t.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                        t.role === "admin" ? "bg-slate-900 text-white" : "bg-blue-100 text-blue-800"
                      }`}>
                        {t.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {new Date(t.created_at).toLocaleDateString("cs-CZ")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {t.id !== me.id && (
                        <button
                          onClick={() => remove(t.id, t.email)}
                          className="text-red-600 hover:text-red-800 font-bold text-xs"
                          data-testid={`btn-delete-teacher-${t.email}`}
                        >
                          Smazat
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={create} className="bg-white rounded-lg p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-heading font-black tracking-tight">Nový účet</h2>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Jméno</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-teacher-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">E-mail</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-teacher-email" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Heslo (min 8 znaků)</label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-teacher-password" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-teacher-role">
                <option value="lecturer">Lektor</option>
                <option value="admin">Master admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 font-bold">Zrušit</button>
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2 text-sm" data-testid="btn-create-teacher">
                Vytvořit
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
